/**
 * A customer's running account with the business (חובות ויתרות). One signed number per
 * customer — `ICustomer.accountBalance` — where a positive value is credit the customer
 * holds (יתרה, usable as a discount on any payment) and a negative value is a debt they
 * owe (חוב). Every change is recorded as an ICustomerBalanceTransaction (view-only history).
 */
export enum CustomerBalanceTransactionTypeEnum {
  /** Staff recorded a debt — balance goes down */
  DebtAdded = 'debt-added',
  /** Staff recorded credit in the customer's favour — balance goes up */
  CreditAdded = 'credit-added',
  /** A payment settled (part of) the debt — balance goes up */
  DebtPayment = 'debt-payment',
  /** Credit was used as a discount on a payment — balance goes down */
  CreditUsed = 'credit-used',
  /** A refunded debt payment — the refunded part of the debt is owed again (balance goes down) */
  DebtReinstated = 'debt-reinstated',
  /** A refunded payment that used credit — that credit is given back (balance goes up) */
  CreditRestored = 'credit-restored',
}

export interface ICustomerBalanceTransaction {
  id?: string;
  bid: string;
  customerId: string;
  type: CustomerBalanceTransactionTypeEnum;
  /** Signed change applied to the balance (+ credit / − debt) */
  amount: number;
  /** The customer's balance right after this change */
  balanceAfter: number;
  note?: string;
  /** Staff member who recorded it (manual entries) */
  createdBy?: { userId: string; name?: string };
  /** Payment that caused it (debt payment / credit used) */
  paymentRequestUuid?: string;
  /** Payment whose refund caused it (debt reinstated / credit restored) */
  refundedPaymentUuid?: string;
  createdAt?: Date | string;
}

export interface ICustomerBalanceResponse {
  balance: number;
  history: ICustomerBalanceTransaction[];
}

/** Body of POST manage/customers/balance — a manual debt or credit entry */
export interface IAddCustomerBalanceEntryRequest {
  customerId: string;
  kind: 'debt' | 'credit';
  /** Always positive; `kind` decides the direction */
  amount: number;
  note?: string;
}
